/**
 * Responsibility: the little a shelf tile needs to know about a card.
 *
 * Three pages now show the same wall of cards - what you own, what you
 * liked, what you are watching - and the rows behind them are three
 * different shapes. A holding has a grade and a quantity; a liked card has
 * a price from the day it was kept; a watched card has a population and a
 * baseline. Giving the tile any one of those types would mean fabricating
 * the other two's fields, which puts invented data on screen.
 *
 * So each source maps onto this, and the tile renders what is actually
 * there. `caption` is already formatted because what is worth saying under
 * a card differs per page and is not the tile's judgement to make.
 */
import type { ArchivedCard, CollectionItem, MarketResult } from '../../api/client';
import { money } from '../grading/money';

export type ShelfCard = {
  /** Stable per source: a holding id, a product key, a discovery key. */
  key: string;
  name: string;
  imageUrl: string | null;
  isGraded: boolean;
  gradeLabel: string | null;
  gradingCompany: string | null;
  quantity: number;
  /** One line under the name, already formatted, or null for nothing. */
  caption: string | null;
};

/** A holding: graded or not, and worth what the whole position is worth. */
export function fromHolding(item: CollectionItem): ShelfCard {
  return {
    key: item.id,
    name: item.name,
    imageUrl: item.image_url,
    isGraded: item.is_graded,
    gradeLabel: item.grade_label,
    gradingCompany: item.grading_company,
    quantity: item.quantity,
    caption: item.holding_value === null ? 'unvalued' : money(item.holding_value),
  };
}

/**
 * A catalogue card, priced as it stands now - a search result or a liked one.
 *
 * Never framed: neither liking a card nor finding one says anything about
 * what condition any copy of it is in. The caption is the best grade's
 * price, which is the figure the row was ranked on.
 */
export function fromMarketCard(card: MarketResult): ShelfCard {
  return {
    key: card.key,
    name: card.name,
    imageUrl: card.image_url,
    isGraded: false,
    gradeLabel: null,
    gradingCompany: null,
    quantity: 1,
    caption: card.best_price
      ? `${money(card.best_price)} ${card.best_label ?? ''}`.trim()
      : 'never sold',
  };
}

/**
 * A card set aside on the discovery board.
 *
 * The caption is what its PSA 10 is worth now, because the only reason to
 * watch one is to see whether that moves.
 */
export function fromWatched(card: ArchivedCard): ShelfCard {
  return {
    key: card.discovery_key,
    name: card.card_name,
    // BetterPop rows carry no render, so the frame has nothing to hold.
    imageUrl: null,
    isGraded: false,
    gradeLabel: null,
    gradingCompany: null,
    quantity: 1,
    caption: card.latest_psa10
      ? `${money(card.latest_psa10)} PSA 10`
      : `${card.set_name} #${card.card_number}`,
  };
}
